import { Box, Button, Checkbox, CircularProgress, Radio, Skeleton, Slide, Typography, useTheme } from '@mui/material'
import { useSearchParams } from 'react-router-dom'
import React, { useEffect, useRef, useState } from 'react'

import thinkingSimba from '../../../../assets/simba-ai/Thinking.png'
import { useSimbaAIStore } from '../../../../store/simbaai.store'
import { Icons } from '../../../../utils/utilities'
import { observer } from 'mobx-react'
import ReportIssue from '../ReportIssue'
import CustomChip from '../../../common/CustomChip'
import Markdown from '../../../../shared/components/common/custom-markdown/Markdown'
import { CustomCodeBlockReadOnly } from '../../../../shared/components/common/custom-markdown/CustomCodeBlock'

const QuizQuestion = observer( () => {

    const [submitting, setSubmitting] = useState( false )

    const { layout, palette, border } = useTheme()
    const SimbaAIStore = useSimbaAIStore()

    const setSearchParams = useSearchParams().at( 1 )

    const quizContainerRef = useRef()

    const backToHome = async () => {
        SimbaAIStore.cancelQuestionStream()
        SimbaAIStore.setView( "home", "right" )
        setSearchParamsValues( { practiceQuiz: "" } )
        try {
            await window.document.exitFullscreen()
        } catch ( err ) {

        }
    }

    const setSearchParamsValues = ( values ) => {
        setSearchParams( prev => {
            const newParams = new URLSearchParams( prev )
            for ( const key in values ) {
                if ( !Object.hasOwn( values, key ) ) continue
                if ( values[key] )
                    newParams.set( key, values[key] )
                else
                    newParams.delete( key )
            }
            return newParams
        }, { replace: true } )
    }

    const nextQuestion = () => {
        const { expectedTotal } = SimbaAIStore.getQuestionStream
        const totalQuestions = expectedTotal || SimbaAIStore.getPracticeSessionQuestions.length
        let nextIndex = SimbaAIStore.getQuizState.questionIndex + 1
        if ( nextIndex >= totalQuestions )
            return
        SimbaAIStore.setQuizState( { questionIndex: nextIndex } )
    }

    const prevQuestion = () => {
        let prevIndex = SimbaAIStore.getQuizState.questionIndex - 1
        if ( prevIndex < 0 )
            return
        SimbaAIStore.setQuizState( { questionIndex: prevIndex } )
    }

    const selectAnswer = async answer => {
        const { questionIndex, userAnswers } = SimbaAIStore.getQuizState

        const question = SimbaAIStore.getPracticeSessionQuestions[SimbaAIStore.getQuizState.questionIndex]

        const newAnswers = [...userAnswers]

        if ( question.correct_answers.length > 1 ) {

            if ( !Array.isArray( newAnswers[questionIndex] ) )
                newAnswers[questionIndex] = []

            const answerIndex = newAnswers[questionIndex].findIndex( ans => ans === answer )
            if ( answerIndex >= 0 ) {
                newAnswers[questionIndex].splice( answerIndex, 1 )
            } else {
                newAnswers[questionIndex].push( answer )
            }

            SimbaAIStore.setQuizState( { userAnswers: newAnswers } )
        } else {
            newAnswers[questionIndex] = answer
            SimbaAIStore.setQuizState( { userAnswers: newAnswers } )
        }
    }

    const submitAndViewScores = async () => {
        try {
            setSubmitting( true )
            const { userAnswers } = SimbaAIStore.getQuizState
            const response = await SimbaAIStore.updatePracticeData( { user_answers: userAnswers } )
            if ( response ) {
                SimbaAIStore.playSound( "practiceCompleted" )
                SimbaAIStore.setView( "result", "left", false )
                setSearchParamsValues( { practiceQuiz: "" } )
                if ( window.document.fullscreenElement )
                    window.document.exitFullscreen()
            }
        } catch ( err ) {
            console.log( err )
        } finally {
            setSubmitting( false )
        }
    }

    const retryGeneration = async () => {
        const practiceSessionId = SimbaAIStore.getQuizState.practiceQuizDetails?.practice_session_id
        if ( !practiceSessionId ) return
        SimbaAIStore.setView( "loading" )
        const success = await SimbaAIStore.fetchPracticeSessionQuestions( practiceSessionId )
        if ( success ) SimbaAIStore.setView( "question", "left", false )
    }

    const { status: streamStatus, error: streamError, expectedTotal } = SimbaAIStore.getQuestionStream
    const questions = SimbaAIStore.getPracticeSessionQuestions
    const { questionIndex } = SimbaAIStore.getQuizState
    const totalQuestions = expectedTotal || questions.length
    const isLastQuestion = questionIndex >= totalQuestions - 1
    const canSubmit = streamStatus !== "streaming" && streamStatus !== "error"

    const questionDetails = questions[questionIndex]
    const questionLoading = !questionDetails
    if ( questionLoading && questionIndex >= totalQuestions ) return null



    return (
        <Box sx={{ overflowX: "hidden", overflowY: "auto", background: "inherit" }} ref={quizContainerRef} display="flex" flexGrow={1} flexDirection="column" justifyContent={{ md: "flex-start", xs: "flex-start" }} gap={layout.cardsGap}>

            <Box flexShrink={0} margin={layout.pagePadding}>
                {/* <Typography fontWeight="800" gutterBottom fontSize="14px">{SimbaAIStore.getQuizState.attemptDetails?.practice_session_details?.title}</Typography> */}
                <Typography fontWeight="800" gutterBottom fontSize="14px">Question {questionIndex + 1} of {totalQuestions}</Typography>
                <Box overflow="clip" height="16px" borderRadius="20px" bgcolor={palette.greyed.light + "55"}>
                    <Box
                        className="animation-wave"
                        borderRadius="100px"
                        sx={{
                            background: palette.primary.dark,
                            transition: "width 400ms",
                            position: "relative",
                            ":before": {
                                content: `''`,
                                borderRadius: "100px",
                                height: "3px",
                                width: "calc(100% - 10px)",
                                top: "4px",
                                left: "5px",
                                position: "absolute",
                                background: `#ffffff`,
                                transition: "width 400ms",
                                opacity: 0.2
                            }
                        }}
                        height="100%"
                        width={`${( 100 * questionIndex ) / totalQuestions}%`}
                    />
                </Box>
                {streamStatus === "error" && <Box marginTop="10px" padding="10px" borderRadius="10px" border={border[1]} bgcolor={palette.error.main + "22"} display="flex" gap="10px" alignItems="center" justifyContent="space-between" flexWrap="wrap">
                    <Typography variant='subtitle2' color='error'>{streamError || "Failed to generate the remaining questions."}</Typography>
                    <Button size='small' onClick={retryGeneration} startIcon={Icons.default.Replay} variant='contained' disableElevation color='error' sx={{ borderRadius: "10px" }}>Retry</Button>
                </Box>}
            </Box>

            <Slide in direction='left' key={SimbaAIStore.getQuizState.questionIndex} >
                {questionLoading ? (
                    <Box overflow="auto" flexShrink={0} padding={layout.pagePadding} display="flex" alignItems={{ xs: "center", md: "flex-start" }} justifyContent="center" gap="20px" flexGrow={1} flexDirection={{ xs: "column", sm: "row" }}>
                        <Box component="img" sx={{ width: { xs: "20vw", sm: "30%" }, maxWidth: 200 }} src={thinkingSimba} />
                        <Box minWidth={0} width={{ xs: "100%", md: "auto" }} flexGrow={1} display="flex" justifyContent="center" flexDirection="column" gap="10px">
                            <Skeleton variant="text" animation="wave" width="90%" height={40} />
                            <Skeleton variant="text" animation="wave" width="60%" height={40} sx={{ marginBottom: "10px" }} />
                            <Box flexGrow={1} minWidth={0} display="flex" gap="10px" flexDirection="column">
                                {[0, 1, 2, 3].map( index => (
                                    <Skeleton key={index} variant="rounded" animation="wave" height={56} width="100%" sx={{ borderRadius: "10px" }} />
                                ) )}
                            </Box>
                        </Box>
                    </Box>
                ) : (
                    <Box overflow="auto" flexShrink={0} padding={layout.pagePadding} display="flex" alignItems={{ xs: "center", md: "flex-start" }} justifyContent="center" gap="20px" flexGrow={1} flexDirection={{ xs: "column", md: "row" }}>
                        <Box component="img" sx={{ width: { xs: "20vw", sm: "30%" }, maxWidth: 200 }} src={thinkingSimba} />
                        <Box minWidth={0} width={{ xs: "100%", md: "auto" }} flexGrow={1} display="flex" justifyContent="center" flexDirection="column">
                            <Typography component="div">
                                <Markdown components={{ code: CustomCodeBlockReadOnly }} styles={{ fontSize: questionDetails.question?.length > 100 ? "18px" : "26px", overflow: "auto", marginBlockStart: 0 }} >
                                    {questionDetails.question}
                                </Markdown>
                            </Typography>

                            {questionDetails.correct_answers?.length > 1 && <Box marginBottom="20px">
                                <CustomChip startIcon={Icons.default.Info} variant='filled' color="customThemeColor" value="Choose multiple correct answers." />
                            </Box>}

                            <Box flexGrow={1} minWidth={0} width="100%" display="flex" gap="10px" flexDirection="column">
                                {questionDetails.answers.map( ( option, index ) => {

                                    const selected = SimbaAIStore.getQuizState.userAnswers[SimbaAIStore.getQuizState.questionIndex] === option || ( Array.isArray( SimbaAIStore.getQuizState.userAnswers[SimbaAIStore.getQuizState.questionIndex] ) && SimbaAIStore.getQuizState.userAnswers[SimbaAIStore.getQuizState.questionIndex]?.includes( option ) )

                                    return (
                                        <Box
                                            component="button"
                                            width="100%"
                                            maxWidth="100%"
                                            boxSizing="border-box"
                                            minWidth={0}
                                            className={selected ? "animation-wave" : ""}
                                            padding={{ md: "10px 20px", xs: "10px" }}
                                            borderRadius="10px"
                                            border={`2px solid ${palette.borderColor}`}
                                            borderBottom={`4px solid ${palette.borderColor}`}
                                            color={palette.text.secondary}
                                            key={option}
                                            display="flex"
                                            gap="10px"
                                            alignItems="flex-start"
                                            height="fit-content"
                                            sx={{
                                                background: `${selected ? palette.primary.main : "none"}`,
                                                transition: "background 200ms",
                                                borderColor: selected ? palette.primary.light : palette.borderColor,
                                                color: selected ? palette.common.white : palette.text.secondary,
                                                cursor: "pointer",
                                                userSelect: "none",
                                                ":hover": {
                                                    borderColor: selected ? palette.primary.light : palette.primary.light,
                                                    color: selected ? palette.common.white : palette.text.primary
                                                }
                                            }}
                                            onClick={() => selectAnswer( option )}
                                        >
                                            {questionDetails.correct_answers?.length > 1 && <Box marginLeft="-10px" textAlign="left" flexShrink={0} display="flex" alignItems='center' justifyContent="center" color='inherit' component="span" height="30px" borderRadius="50%" >
                                                <Checkbox
                                                    sx={{
                                                        color: "GrayText",
                                                        '&.Mui-checked': {
                                                            color: "white",
                                                        },
                                                    }}
                                                    checked={Boolean( selected ) || false} />
                                            </Box>}
                                            {questionDetails.correct_answers?.length === 1 && <Box marginLeft="-10px" textAlign="left" flexShrink={0} display="flex" alignItems='center' justifyContent="center" color='inherit' component="span" height="30px" borderRadius="50%" >
                                                <Radio
                                                    sx={{
                                                        color: "GrayText",
                                                        '&.Mui-checked': {
                                                            color: "white",
                                                        },
                                                    }}
                                                    name={`question-${SimbaAIStore.getQuizState.questionIndex}`}
                                                    value={`option-${index}`}
                                                    checked={Boolean( selected ) || false}
                                                />
                                            </Box>}
                                            <Box minWidth={0} flexGrow={1}>
                                                <Markdown components={{ code: CustomCodeBlockReadOnly }} styles={{ fontSize: { md: "18px", xs: "14px" }, overflow: "auto", marginBlockStart: 0, textAlign: "left", color: "inherit", "& p": { marginBottom: "0 !important" } }} >
                                                    {option}
                                                </Markdown>
                                            </Box>
                                        </Box>
                                    )
                                } )}
                            </Box>
                        </Box>
                    </Box>
                )}
            </Slide>
            <Box display="flex" position="sticky" bottom="0" justifyContent="space-between" alignItems="center" borderTop={`4px dashed ${palette.borderColor}`} bgcolor={palette.contentBg} padding={{ md: "30px 20px", xs: "10px" }}>
                <Box display="flex" gap="10px" alignItems="center">
                    <Button onClick={backToHome} sx={{ borderBottom: `4px solid ${palette.error.light + "99"}`, borderRadius: "10px" }} startIcon={Icons.default.ArrowBack} variant='outlined' color='error' >Quit Practice</Button>
                    <ReportIssue key={SimbaAIStore.getQuizState.questionIndex} />
                </Box>

                <Box display="flex" gap="10px" alignItems="center">

                    {!isLastQuestion && <Button
                        disabled={
                            !SimbaAIStore.getQuizState.userAnswers[questionIndex] ||
                            ( questionDetails?.correct_answers?.length > 1 && SimbaAIStore.getQuizState.userAnswers[questionIndex].length < 2 )
                        }
                        onClick={nextQuestion}
                        sx={{ borderBottom: `4px solid ${palette.primary.light + "99"}`, borderRadius: "10px" }}
                        endIcon={Icons.default.ArrowForward}
                        variant='contained'
                        disableElevation
                        color='primary'
                    >
                        Next
                    </Button>}
                    {isLastQuestion && <Button
                        disabled={
                            submitting || !canSubmit ||
                            !SimbaAIStore.getQuizState.userAnswers[questionIndex] ||
                            ( questionDetails?.correct_answers?.length > 1 && SimbaAIStore.getQuizState.userAnswers[questionIndex].length < 2 )
                        }
                        className={SimbaAIStore.getQuizState.userAnswers[questionIndex] ? 'animation-wave' : ""}
                        onClick={submitAndViewScores}
                        sx={{ borderBottom: `4px solid ${palette.primary.light + "99"}`, borderRadius: "10px" }}
                        startIcon={submitting || streamStatus === "streaming" ? <CircularProgress size={20} /> : Icons.default.CheckIcon}
                        variant='contained'
                        disableElevation
                        color='primary'
                    >
                        {submitting ? "Submitting" : streamStatus === "streaming" ? "Finalizing" : "Submit"}
                    </Button>}
                </Box>
            </Box>
        </Box>
    )
}
)
export default QuizQuestion